import type { FC } from 'react';
import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/Logout';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useAuth } from 'src/hooks/use-auth';
import { useRouter } from 'src/hooks/use-router';
import { useSettings } from 'src/hooks/use-settings';
import { useVersion } from 'src/hooks/use-version';
import { paths } from 'src/paths';
import { Issuer } from 'src/utils/auth';
import { storageApi, BUCKETS } from 'src/api/supabase/storage';
import { supabase } from 'src/api/supabase/client';

interface AccountPopoverProps {
  anchorEl: null | Element;
  onClose?: () => void;
  open?: boolean;
  onAvatarChange?: (url: string) => void;
}

export const AccountPopover: FC<AccountPopoverProps> = (props) => {
  const { anchorEl, onClose, open, onAvatarChange, ...other } = props;
  const router = useRouter();
  const auth = useAuth();
  const settings = useSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const isDarkMode = settings.paletteMode === 'dark';
  const { version, setVersion } = useVersion();

  const handleToggleTheme = useCallback(() => {
    settings.handleUpdate({
      paletteMode: isDarkMode ? 'light' : 'dark',
    });
  }, [isDarkMode, settings]);

  const handleChangePhoto = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const email = auth.user?.email;
    if (!email) {
      toast.error('Not logged in');
      return;
    }

    setUploading(true);
    try {
      const result = await storageApi.uploadImage(
        BUCKETS.AVATARS,
        file,
        email,
        { maxWidth: 400, maxHeight: 400, quality: 0.85 }
      );

      if (result.success && result.file?.url) {
        const url = result.file.url;
        const { error: upsertError } = await supabase
          .from('user_profiles')
          .upsert({ email, avatar_url: url }, { onConflict: 'email' });
        if (upsertError) {
          toast.error('Saved photo but failed to persist');
        } else {
          toast.success('Profile photo updated');
        }
        onAvatarChange?.(url);
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [auth.user?.email, onAvatarChange]);

  const handleLogout = useCallback(
    async (): Promise<void> => {
      try {
        onClose?.();

        switch (auth.issuer) {
          case Issuer.Amplify: {
            await auth.signOut();
            break;
          }

          case Issuer.Auth0: {
            await auth.logout();
            break;
          }

          case Issuer.Firebase: {
            await auth.signOut();
            break;
          }

          case Issuer.JWT: {
            await auth.signOut();
            break;
          }

          default: {
            // console.warn('Using an unknown Auth Issuer, did not log out');
          }
        }

        router.push(paths.index);
      } catch (err) {
        // console.error(err);
        toast.error('Something went wrong!');
      }
    },
    [auth, router, onClose]
  );

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom'
      }}
      disableScrollLock
      onClose={onClose}
      open={!!open}
      PaperProps={{
        sx: {
          width: 200,
          bgcolor: isDarkMode ? '#1a1a1c' : '#ffffff',
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          borderRadius: '12px',
          boxShadow: isDarkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.15)',
        }
      }}
      {...other}
    >
      <Box sx={{ p: 2 }}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileSelect}
        />

        {/* Change Photo */}
        <Button
          color="inherit"
          onClick={handleChangePhoto}
          disabled={uploading}
          fullWidth
          startIcon={uploading
            ? <CircularProgress size={16} color="inherit" />
            : <CameraAltIcon sx={{ fontSize: 18 }} />
          }
          sx={{
            py: 1,
            mb: 1.5,
            fontWeight: 500,
            fontSize: '0.85rem',
            color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            justifyContent: 'flex-start',
            borderRadius: '8px',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            },
          }}
        >
          {uploading ? 'Uploading...' : 'Change Photo'}
        </Button>

        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', mb: 1.5 }} />

        {/* Theme Toggle */}
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            mb: 1,
          }}
        >
          Appearance
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            onClick={() => settings.handleUpdate({ paletteMode: 'light' })}
            variant={!isDarkMode ? 'contained' : 'outlined'}
            size="small"
            startIcon={<LightModeIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              py: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: '8px',
              bgcolor: !isDarkMode ? '#2970FF' : 'transparent',
              color: !isDarkMode ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: !isDarkMode ? '#1d5ed8' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Light
          </Button>
          <Button
            onClick={() => settings.handleUpdate({ paletteMode: 'dark' })}
            variant={isDarkMode ? 'contained' : 'outlined'}
            size="small"
            startIcon={<DarkModeIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1,
              py: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: '8px',
              bgcolor: isDarkMode ? '#2970FF' : 'transparent',
              color: isDarkMode ? '#fff' : 'rgba(0,0,0,0.7)',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: isDarkMode ? '#1d5ed8' : 'rgba(0,0,0,0.05)',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Dark
          </Button>
        </Stack>

        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', mb: 1.5 }} />

        {/* Version Toggle */}
        <Typography
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
            mb: 1,
          }}
        >
          Version
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            onClick={() => setVersion('1')}
            variant={version === '1' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              flex: 1,
              py: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: '8px',
              bgcolor: version === '1' ? '#2970FF' : 'transparent',
              color: version === '1' ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: version === '1' ? '#1d5ed8' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Classic
          </Button>
          <Button
            onClick={() => setVersion('2')}
            variant={version === '2' ? 'contained' : 'outlined'}
            size="small"
            sx={{
              flex: 1,
              py: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              borderRadius: '8px',
              bgcolor: version === '2' ? '#2970FF' : 'transparent',
              color: version === '2' ? '#fff' : (isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
              '&:hover': {
                bgcolor: version === '2' ? '#1d5ed8' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
                borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
              },
            }}
          >
            Pro
          </Button>
        </Stack>

        <Divider sx={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', mb: 1.5 }} />

        {/* Logout Button */}
        <Button
          color="inherit"
          onClick={handleLogout}
          fullWidth
          startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
          sx={{
            py: 1,
            fontWeight: 500,
            fontSize: '0.85rem',
            color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
            justifyContent: 'flex-start',
            borderRadius: '8px',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              color: '#EF4444',
            },
          }}
        >
          Logout
        </Button>
      </Box>
    </Popover>
  );
};

AccountPopover.propTypes = {
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  open: PropTypes.bool
};
